import React, { useMemo, useState, useContext, useEffect, useReducer } from 'react'
import { Icon, Header, Breadcrumb, Table, Button, Modal, Popup, TransitionablePortal, Form, Divider, Segment } from 'semantic-ui-react'
import { useSubscription } from 'utils'
import request from 'services/http'
import { tap, finalize } from 'rxjs/operators'
import { numFormat, dateIdToFormat } from '@rededompedro/utils'
import { GlobalContext } from 'globalContext'
import InputMask from 'react-input-mask'
import { DateTime } from 'luxon';
// import history from 'services/history'
import './UploadNF.scss'

function fixUnidadeName(name) {
    let match = name.match(/\((.*)\)/)
    if (match) {
        match = match[1].trim()
        return !['DP 1', 'DP 2'].includes(match) ? match.replace('DP ', '') : match
    }
    return name
}

function shortClienteName(name) {
    return <Popup
        trigger={<span>{name.slice(0, 18) + '...'}</span>}
        content={name}
        position='top left'
    />
}

const initialFormValues = {
    pedido_id: null,
    periodo_ini: null,
    periodo_fim: null,
    unidades: []
}

function formReducer(state, action) {
    if ('reset' in action) {
        if (action.reset) {
            return initialFormValues
        }
    }

    if (!(action.field in state)) {
        return state
    }
    return { ...state, [action.field]: action.payload }
}

export default function UploadNotaFiscal(props) {

    const [formValues, setFormState] = useReducer(formReducer, initialFormValues)

    const [data, setData] = useState([])
    const [loadData, setLoadData] = useState(false)

    const [currentOrderId, setCurrentOrderId] = useState(null)
    const [transactions, setTransactions] = useState([])
    const [openModal, setOpenModal] = useState(false)
    const [globalState] = useContext(GlobalContext)

    const get$ = useMemo(() => {
        const params = Object.fromEntries(new URLSearchParams(props.location.search))
        return request({ method: 'GET', url: '/cartao/upload-nf', params }).pipe(
            tap(({ response }) => setData(response)),
            finalize(() => setLoadData(false))
        )
    }, [props.location.search])
    useSubscription(get$, loadData)

    const getTransacoes$ = useMemo(() => request({ method: 'GET', url: '/cartao/upload-nf/transacoes', params: { pedido_id: currentOrderId } }).pipe(
        tap(({ response }) => setTransactions(response)),
        tap(() => setOpenModal(true)),
        finalize(() => setCurrentOrderId(null))
    ), [currentOrderId])
    useSubscription(getTransacoes$, currentOrderId)

    useEffect(() => {
        setLoadData(true)
    }, [props.location.search])

    function handleFieldChange(ev, data) {
        const target = ev.target
        const text = !data ? target.value : data.value
        const field = !data ? target.name : data.name
        setFormState({ field, text })
    }

    function onSearch() {
        let params = {}
        for (let field in formValues) {
            switch(field) {
                case 'pedido_id':
                    params[field] = parseInt(formValues[field])
                break;
    
                case 'periodo_ini':
                case 'periodo_fim':
                    const date = DateTime.fromFormat(formValues[field], 'dd/MM/yyyy')
                    params[field] = date.isValid ? date.toFormat('yyyy-MM-dd') : null
                break;
    
                case 'unidades':
                    params[field] = formValues[field].join()
                break;
    
                default:
                    params[field] = formValues[field]
            }
        }

        console.log(params)

        // todo: melhorar ou mudar para data_ini e data_fim separados
        // if (('periodo_ini' in params) && ('periodo_fim' in params)) {
        //     params['periodo'] = [params['periodo_ini'], params['periodo_fim']]
        //     delete params['periodo_ini']
        //     delete params['periodo_fim']
        // }
        // console.log(params)
        // history.push({ search: Object.keys(params).map(key => key + '=' + params[key]).join('&') })
    }

    return (
        <div id="cartao-upload-nf">
            <TransitionablePortal open={openModal && transactions.length > 0}>
                <Modal size='fullscreen' open={true} onClose={() => setOpenModal(false)} closeIcon>
                    <Modal.Header>Transações</Modal.Header>
                    <Modal.Content>
                        <Table size='small' selectable>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>ID Transação</Table.HeaderCell>
                                    <Table.HeaderCell>Num. Vinc.</Table.HeaderCell>
                                    <Table.HeaderCell>Numero NF</Table.HeaderCell>
                                    <Table.HeaderCell>Data/Hora Portal</Table.HeaderCell>
                                    <Table.HeaderCell>Data/Hora A.S</Table.HeaderCell>
                                    <Table.HeaderCell>Combustível</Table.HeaderCell>
                                    <Table.HeaderCell>Placa Portal</Table.HeaderCell>
                                    <Table.HeaderCell>Placa A.S</Table.HeaderCell>
                                    <Table.HeaderCell>Litros</Table.HeaderCell>
                                    <Table.HeaderCell>Valor</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {transactions.map((t, i) => (
                                    <Table.Row key={i} className={t.num_vinc === 0 && 'no-vinc'}>
                                        <Table.Cell>{t.id}</Table.Cell>
                                        <Table.Cell>{t.num_vinc}</Table.Cell>
                                        <Table.Cell>{t.numero_nota}</Table.Cell>
                                        <Table.Cell>{dateIdToFormat(t.data_hora_portal, 'dd/MM/yyyy HH:mm')}</Table.Cell>
                                        <Table.Cell>{dateIdToFormat(t.data_hora_as, 'dd/MM/yyyy HH:mm')}</Table.Cell>
                                        <Table.Cell>{t.combustivel}</Table.Cell>
                                        <Table.Cell>{t.placa_portal}</Table.Cell>
                                        <Table.Cell>{t.placa_as}</Table.Cell>
                                        <Table.Cell>{numFormat(t.litros)} L</Table.Cell>
                                        <Table.Cell>R$ {numFormat(t.valor)}</Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </Modal.Content>
                </Modal>
            </TransitionablePortal>
            <div className="subheader">
                <Breadcrumb style={{ zIndex: 999 }} size='mini' icon='right angle' sections={[
                    { key: 'home', content: 'Home', link: true },
                    { key: 'cartao', content: 'Cartão', link: true },
                    { key: 'upload-nf', content: 'Upload da Nota Fiscal', active: true }
                ]} />
                <Header size='medium' as='h2'>
                    <Icon name='file alternate outline' />
                    <Header.Content>Upload da Nota Fiscal
                    <Header.Subheader style={{ fontSize: 15 }}>Relatório do BOT</Header.Subheader>
                    </Header.Content>
                </Header>
                <Divider fitted style={{ margin: '0 12px 10px 12px', borderTopWidth: '2px' }} />
                <Form style={{ padding: '0 20px' }}>
                    <Form.Group>
                        <Form.Input name='pedido_id' label='ID Pedido' width='2' value={formValues.pedido_id} onChange={handleFieldChange} />
                        <Form.Field className='date-range' width='4' required>
                            <label>Período do Pedido</label>
                            <div className="d-flex">
                                <InputMask name='periodo_ini' mask="99/99/9999" style={{ marginRight: 5, textAlign: 'center' }} value={formValues.periodo_ini} onChange={handleFieldChange} />
                                <InputMask name='periodo_fim' mask="99/99/9999" style={{ textAlign: 'center' }} value={formValues.periodo_fim} onChange={handleFieldChange} />
                            </div>
                        </Form.Field>
                        <Form.Dropdown name='unidades' size='8' width='4' selection search multiple clearable label='Unidades' options={(globalState.empresas || []).map((e, i) => ({ key: i, value: e._id, text: e.nomeEmpresa }))} value={formValues.unidades} onChange={handleFieldChange} />
                        <Form.Field style={{ display: 'flex', alignItems: 'flex-end', marginBottom:1 }}>
                            <Button content='Buscar' icon='search' primary onClick={onSearch} />
                        </Form.Field>
                    </Form.Group>
                </Form>
            </div>
            <div id="content">
                {data.length > 0 ? 
                    <Table selectable>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Pedido ID</Table.HeaderCell>
                                <Table.HeaderCell>Data Pedido</Table.HeaderCell>
                                <Table.HeaderCell>Unidade</Table.HeaderCell>
                                <Table.HeaderCell>Cliente</Table.HeaderCell>
                                <Table.HeaderCell>% Enviado NF</Table.HeaderCell>
                                <Table.HeaderCell>Transações</Table.HeaderCell>
                                <Table.HeaderCell>Valor Total</Table.HeaderCell>
                                <Table.HeaderCell>Valor Total Notas</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {data.map((d, i) => (
                                <Table.Row key={i}>
                                    <Table.Cell>{d.id}</Table.Cell>
                                    <Table.Cell>{dateIdToFormat(d.data_faturamento, 'dd/MM/yyyy')}</Table.Cell>
                                    <Table.Cell>{fixUnidadeName(d.unidade_nome)}</Table.Cell>
                                    <Table.Cell>{shortClienteName(d.razao_social)}</Table.Cell>
                                    <Table.Cell>{numFormat(d.percentual_enviado)} %</Table.Cell>
                                    <Table.Cell textAlign='center'><Button onClick={() => setCurrentOrderId(d.id)} content={d.qtd_transacoes} loading={currentOrderId === d.id} /></Table.Cell>
                                    <Table.Cell>R$ {numFormat(d.valor_total)}</Table.Cell>
                                    <Table.Cell>R$ {numFormat(d.valor_total_notas)}</Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                : <Segment placeholder style={{ minHeight: 12 }}><Header icon><Icon name='inbox' />Oops, dados não encontrados.</Header></Segment>}
            </div>
        </div>
    )
}
